import React, { useEffect, useState } from "react";
import CrmLayout from "../../components/CrmLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { prettyDate, money } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Bell, Check, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  waiting: "bg-accent/20 text-accent-foreground", offered: "bg-primary/10 text-primary",
  booked: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", expired: "bg-muted text-muted-foreground",
};
const WINDOW_LABEL = { any: "Any time", morning: "Mornings", afternoon: "Afternoons", evening: "Evenings", weekday: "Weekdays", weekend: "Weekends" };

export default function Waitlist() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("all");
  const load = (p = period) => {
    api.get("/waitlist").then((r) => setItems(r.data)).catch(() => {});
    api.get("/waitlist/stats", { params: { period: p } }).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(period); }, [period]);

  const notify = async (id) => {
    try { const { data } = await api.post(`/waitlist/${id}/notify`); toast.success(data.sent ? "Offer sent to client" : "No email/phone on file"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/waitlist/${id}/status`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/waitlist/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const clearExpired = async () => {
    try { const { data } = await api.post("/waitlist/clear-expired"); toast.success(`Cleared ${data.deleted} expired ${data.deleted === 1 ? "entry" : "entries"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hasExpired = items.some((w) => w.status === "expired");

  return (
    <CrmLayout title="Waitlist">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow text-muted-foreground">Waitlist performance</div>
        <select data-testid="stats-period" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="h-9 rounded-md border border-border bg-card px-3 text-sm">
          <option value="all">All time</option>
          <option value="month">This month</option>
          <option value="30d">Last 30 days</option>
          <option value="7d">Last 7 days</option>
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="waitlist-stats">
        <Stat label="Waiting" value={stats?.waiting ?? "—"} />
        <Stat label="Offers sent" value={stats?.offers_sent ?? "—"} />
        <Stat label="Claimed" value={stats?.claimed ?? "—"} accent />
        <Stat label="Expired / rolled" value={stats?.expired ?? "—"} />
        <Stat label="Revenue recovered" value={stats ? money(stats.revenue_recovered) : "—"} accent />
      </div>

      <div className="bg-card border border-border rounded-lg p-4 mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">When an appointment is cancelled or rescheduled, the longest-waiting matching client is automatically offered the freed slot by email &amp; SMS. Stale and past-dated entries expire automatically so fresh clients aren't blocked.</p>
        <Button variant="outline" size="sm" data-testid="clear-expired-btn" disabled={!hasExpired} onClick={clearExpired}>Clear expired</Button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Service</TableHead><TableHead>Stylist</TableHead>
            <TableHead>Prefers</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No one on the waitlist yet.</TableCell></TableRow>}
            {items.map((w) => (
              <TableRow key={w.id} data-testid={`waitlist-row-${w.id}`}>
                <TableCell><div className="text-sm font-medium">{w.client_name}</div><div className="text-xs text-muted-foreground">{w.client_phone || w.client_email || "—"}</div></TableCell>
                <TableCell className="text-sm">{w.service_name}</TableCell>
                <TableCell className="text-sm">{w.stylist_name}</TableCell>
                <TableCell className="text-sm">
                  <div>{WINDOW_LABEL[w.preferred_window] || "Any time"}</div>
                  <div className="text-xs text-muted-foreground">{w.preferred_date ? prettyDate(w.preferred_date) : "Any date"}</div>
                </TableCell>
                <TableCell>
                  <Badge className={STATUS_STYLE[w.status]}>{w.status}</Badge>
                  {w.status === "offered" && w.offer_expires_at && <div className="text-[10px] text-muted-foreground mt-1">expires {new Date(w.offer_expires_at).toLocaleString()}</div>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" title="Send offer" data-testid={`waitlist-notify-${w.id}`} onClick={() => notify(w.id)}><Bell size={15} /></Button>
                    {w.status !== "booked" && <Button variant="ghost" size="sm" title="Mark booked" data-testid={`waitlist-book-${w.id}`} onClick={() => setStatus(w.id, "booked")}><Check size={15} className="text-[hsl(var(--success))]" /></Button>}
                    <Button variant="ghost" size="sm" className="text-destructive" title="Remove" data-testid={`waitlist-delete-${w.id}`} onClick={() => remove(w.id)}><Trash2 size={15} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </CrmLayout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="label-eyebrow text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent ? "text-accent" : ""}`}>{value}</div>
    </div>
  );
}
